/**
 * TanStack Query hooks for `advanced-retargeting-controls-for-smarter-compliance`.
 * Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	create,
	list,
	remove,
	advancedRetargetingControlsForSmarterComplianceQueryKeys,
	type CreateRetargetingRuleBody,
	type ListFilters,
	type ListRetargetingRulesResponse,
} from "./api";

export function useAdvancedRetargetingControlsForSmarterComplianceList(
	filters?: ListFilters,
): UseQueryResult<ListRetargetingRulesResponse, Error> {
	return useQuery({
		queryKey:
			advancedRetargetingControlsForSmarterComplianceQueryKeys.list(filters),
		queryFn: () => list(filters),
	});
}

export function useAdvancedRetargetingControlsForSmarterComplianceCreate(): UseMutationResult<
	{ ok: true; id: string },
	Error,
	CreateRetargetingRuleBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: create,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: advancedRetargetingControlsForSmarterComplianceQueryKeys.all,
			});
		},
	});
}

export function useAdvancedRetargetingControlsForSmarterComplianceDelete(): UseMutationResult<
	{ ok: true },
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: remove,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: advancedRetargetingControlsForSmarterComplianceQueryKeys.all,
			});
		},
	});
}

/**
 * Toggle a rule's enabled state by re-creating with the same `rule_key`.
 * Backend upsert is idempotent on (tenant_id, rule_key), so a POST with the
 * same key is treated as a no-op insert + returns the existing row id.
 * For a real flip, we recreate with desired enabled state.
 */
export function useAdvancedRetargetingControlsForSmarterComplianceToggle(): UseMutationResult<
	{ ok: true; id: string },
	Error,
	CreateRetargetingRuleBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: create,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: advancedRetargetingControlsForSmarterComplianceQueryKeys.all,
			});
		},
	});
}
